/**
 * My Safe Spaces Website - Header Component
 * ==========================================
 * Two-row header: top utility bar + main navigation
 */

import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, UserPlus, LogIn } from 'lucide-react';
import './Header.css';

const Header = ({ onContactClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeDropdown && !e.target.closest('.has-dropdown')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeDropdown]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const toggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      {/* Top Utility Bar */}
      <div className="top-bar">
        <div className="container">
          <div className="top-bar-inner">
            <div className="top-bar-left">
              <span className="top-bar-link disabled">
                <UserPlus size={14} />
                Become a Partner
              </span>
              <button
                onClick={onContactClick}
                className="top-bar-link"
                type="button"
              >
                Contact Us
              </button>
            </div>
            <div className="top-bar-right">
              <Link to="/taisi" className="top-bar-btn taisi-btn">
                TAISI Members
              </Link>
              <span className="top-bar-btn school-login-btn disabled">
                <LogIn size={14} />
                School Login
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="main-nav">
        <div className="container">
          <nav className="nav">
            <Link to="/" className="logo">
              <img src="/mss/logo.png" alt="My Safe Spaces" />
              <span>My Safe Spaces</span>
            </Link>

            <ul className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
              {/* Solutions Dropdown */}
              <li className={`nav-item has-dropdown ${activeDropdown === 'solutions' ? 'dropdown-open' : ''}`}>
                <button
                  className="nav-link"
                  onClick={() => toggleDropdown('solutions')}
                  aria-expanded={activeDropdown === 'solutions'}
                  aria-haspopup="true"
                >
                  Solutions <ChevronDown size={16} />
                </button>
                <ul className="dropdown-menu" role="menu">
                  <li>
                    <Link to="/schools" className={isActive('/schools') ? 'active' : ''}>
                      <span className="dropdown-icon schools">🏫</span>
                      <div>
                        <strong>Schools (SWISS)</strong>
                        <small>K-12 Mental Wellness</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/higher-education" className={isActive('/higher-education') ? 'active' : ''}>
                      <span className="dropdown-icon higher-ed">🎓</span>
                      <div>
                        <strong>Higher Ed (SWISS)</strong>
                        <small>Colleges & Universities</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/workplace" className={isActive('/workplace') ? 'active' : ''}>
                      <span className="dropdown-icon workplace">🏢</span>
                      <div>
                        <strong>Workplace (CREW)</strong>
                        <small>Corporate Wellness</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/professional" className={isActive('/professional') ? 'active' : ''}>
                      <span className="dropdown-icon professional">🌿</span>
                      <div>
                        <strong>Professional (ELEVATE)</strong>
                        <small>MH Professional Training</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/personal" className={isActive('/personal') ? 'active' : ''}>
                      <span className="dropdown-icon personal">🌱</span>
                      <div>
                        <strong>Personal</strong>
                        <small>Individual Wellness</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/mobile-app" className={isActive('/mobile-app') ? 'active' : ''}>
                      <span className="dropdown-icon mobile">📱</span>
                      <div>
                        <strong>Mobile App</strong>
                        <small>iOS & Android</small>
                      </div>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* Programs Dropdown */}
              <li className={`nav-item has-dropdown ${activeDropdown === 'programs' ? 'dropdown-open' : ''}`}>
                <button
                  className="nav-link"
                  onClick={() => toggleDropdown('programs')}
                  aria-expanded={activeDropdown === 'programs'}
                  aria-haspopup="true"
                >
                  Programs <ChevronDown size={16} />
                </button>
                <ul className="dropdown-menu" role="menu">
                  <li>
                    <Link to="/thrive-academy" className={isActive('/thrive-academy') ? 'active' : ''}>
                      <span className="dropdown-icon thrive">📚</span>
                      <div>
                        <strong>Thrive Academy</strong>
                        <small>Training & Certification</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/intellicare-ai" className={isActive('/intellicare-ai') ? 'active' : ''}>
                      <span className="dropdown-icon intellicare">🤖</span>
                      <div>
                        <strong>IntellicareAI</strong>
                        <small>AI-Powered Support</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/mosaic" className={isActive('/mosaic') ? 'active' : ''}>
                      <span className="dropdown-icon mosaic">📊</span>
                      <div>
                        <strong>MOSAIC Assessments</strong>
                        <small>Wellness Assessment Platform</small>
                      </div>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* Resources Dropdown */}
              <li className={`nav-item has-dropdown ${activeDropdown === 'resources' ? 'dropdown-open' : ''}`}>
                <button
                  className="nav-link"
                  onClick={() => toggleDropdown('resources')}
                  aria-expanded={activeDropdown === 'resources'}
                  aria-haspopup="true"
                >
                  Resources <ChevronDown size={16} />
                </button>
                <ul className="dropdown-menu" role="menu">
                  <li>
                    <Link to="/resources/blogs" className={isActive('/resources/blogs') ? 'active' : ''}>
                      <span className="dropdown-icon blogs">📝</span>
                      <div>
                        <strong>Blogs</strong>
                        <small>Insights & Articles</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/resources/awareness" className={isActive('/resources/awareness') ? 'active' : ''}>
                      <span className="dropdown-icon awareness">🎗️</span>
                      <div>
                        <strong>Awareness</strong>
                        <small>Mental Health Resources</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/webinars" className={isActive('/webinars') ? 'active' : ''}>
                      <span className="dropdown-icon webinars">🎥</span>
                      <div>
                        <strong>Webinars</strong>
                        <small>Live Expert Sessions</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/resources/labs" className={isActive('/resources/labs') ? 'active' : ''}>
                      <span className="dropdown-icon labs">🔬</span>
                      <div>
                        <strong>MSS Labs</strong>
                        <small>Research & Innovation</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/resources/alumni" className={isActive('/resources/alumni') ? 'active' : ''}>
                      <span className="dropdown-icon alumni">🎓</span>
                      <div>
                        <strong>ELEVATE Alumni</strong>
                        <small>Graduate Directory</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/resources/jobs" className={isActive('/resources/jobs') ? 'active' : ''}>
                      <span className="dropdown-icon jobs">💼</span>
                      <div>
                        <strong>Job Board</strong>
                        <small>Counseling Openings</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/resources/elevate-outreach" className={isActive('/resources/elevate-outreach') ? 'active' : ''}>
                      <span className="dropdown-icon outreach">📣</span>
                      <div>
                        <strong>ELEVATE Outreach</strong>
                        <small>Campus Program</small>
                      </div>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* About Dropdown */}
              <li className={`nav-item has-dropdown ${activeDropdown === 'about' ? 'dropdown-open' : ''}`}>
                <button
                  className="nav-link"
                  onClick={() => toggleDropdown('about')}
                  aria-expanded={activeDropdown === 'about'}
                  aria-haspopup="true"
                >
                  About <ChevronDown size={16} />
                </button>
                <ul className="dropdown-menu" role="menu">
                  <li>
                    <Link to="/about" className={isActive('/about') && !location.pathname.includes('/about/') ? 'active' : ''}>
                      <span className="dropdown-icon about">🌟</span>
                      <div>
                        <strong>Our Story</strong>
                        <small>Mission & Vision</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/about/leadership" className={isActive('/about/leadership') ? 'active' : ''}>
                      <span className="dropdown-icon leadership">👥</span>
                      <div>
                        <strong>Leadership</strong>
                        <small>Our Team</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/about/strategic-advisors" className={isActive('/about/strategic-advisors') ? 'active' : ''}>
                      <span className="dropdown-icon advisors">🧠</span>
                      <div>
                        <strong>Strategic Advisors</strong>
                        <small>Expert Guidance</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/about/certification-council" className={isActive('/about/certification-council') ? 'active' : ''}>
                      <span className="dropdown-icon council">🏅</span>
                      <div>
                        <strong>Certification Council</strong>
                        <small>Quality Standards</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/about/partners" className={isActive('/about/partners') ? 'active' : ''}>
                      <span className="dropdown-icon partners">🤝</span>
                      <div>
                        <strong>Partners</strong>
                        <small>Our Collaborators</small>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <a href="https://careers.mysafespaces.org">
                      <span className="dropdown-icon careers">💼</span>
                      <div>
                        <strong>Careers</strong>
                        <small>Join Our Team</small>
                      </div>
                    </a>
                  </li>
                </ul>
              </li>
            </ul>

            {/* Mobile Menu Toggle */}
            <button
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
